import React, { useCallback, useState } from "react";

import { Dialog, Grid } from "@material-ui/core";

import { Label } from "components/Form/FormikControls";
import { Column } from "components/Layout";
import { DialogTitle } from "components/MaterialDialog";
import { Paper } from "components/Paper";
import ReactSelect, {
  findOptionByValue,
} from "components/SearchableSelector/ReactSelect";

import { ModalAdapterProps, ModalTypes } from "constants/navigation";

import { useModalAdapter } from "hooks";
import { useHasMultiSaleReportPermission } from "hooks/useHasPermission";
import { MultiSaleReportsPermissionNotice } from "components/MultiSaleReports/MultiSaleReportsPermissionNotice";

import { BulkCreateReportOptions, Reports } from "constants/reports";
import { pluralize } from "lib/pluralize";
import { useSelector } from "react-redux";
import { getBusinesses } from "selectors";
import { BusinessFinancialStatementForm } from "./FinancialStatement";
import { BusinessDetailedFinancialStatementForm } from "./DetailedFinancialStatement";

const BusinessReports = [
  Reports.FinancialStatementReport.slug,
  Reports.DetailedFinancialStatementReport.slug,
  Reports.OutstandingStatementReport.slug,
];

type BusinessReportSlug = (typeof BusinessReports)[number];

type BusinessReportFormProps = {
  businessIds: string[];
  onClose: () => void;
  reportSlug: string;
};

const ReportComponentMap: Record<
  BusinessReportSlug,
  React.ComponentType<BusinessReportFormProps>
> = {
  FinancialStatementReport: BusinessFinancialStatementForm,
  DetailedFinancialStatementReport: BusinessDetailedFinancialStatementForm,
  OutstandingStatementReport: BusinessDetailedFinancialStatementForm,
};

const ReportOptions = BulkCreateReportOptions.filter(option =>
  BusinessReports.includes(option.value),
);

interface BusinessReportsModalHashParams {
  businessIds: string[];
}
type BusinessReportsModalProps = ModalAdapterProps;

export function BusinessReportsModal(props: BusinessReportsModalProps) {
  const [params, ignored, onClose] =
    useModalAdapter<BusinessReportsModalHashParams>(
      ModalTypes.BusinessReports,
      props,
    );
  const { businessIds } = params;

  const businessByIdLookup = useSelector(getBusinesses);
  const hasMultiSaleReportPermission = useHasMultiSaleReportPermission();

  const [reportSlug, setReportSlug] = useState<BusinessReportSlug>(
    BusinessReports[0],
  );

  const onChangeReportSlug = useCallback(
    option => {
      setReportSlug(option.value);
    },
    [setReportSlug],
  );

  const ReportForm = ReportComponentMap[reportSlug];

  const businessCount = businessIds.length;

  const titleText = `Generate ${pluralize("Report", businessCount)} for ${businessCount === 1 ? businessByIdLookup[businessIds[0]].name : `${businessCount} ${pluralize("Business", businessCount)}`}`;

  return (
    <Dialog open onClose={onClose} fullScreen>
      <DialogTitle onClose={onClose}>{titleText}</DialogTitle>

      <Column padding={2}>
        <Paper>
          {hasMultiSaleReportPermission ? (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Column fullWidth>
                    <Label>Report</Label>
                    <ReactSelect
                      options={ReportOptions}
                      onChange={onChangeReportSlug}
                      value={findOptionByValue(ReportOptions, reportSlug)}
                    />
                  </Column>
                </Grid>
              </Grid>
              <ReportForm
                businessIds={businessIds}
                onClose={onClose}
                reportSlug={reportSlug}
              />
            </>
          ) : (
            <MultiSaleReportsPermissionNotice />
          )}
        </Paper>
      </Column>
    </Dialog>
  );
}
